<template>
  <div class="container">
    <div class="row">
      <div class="col-12">
        <b-form inline>
          <div class="flex-fill mr-2">
            <vue-multiselect
              v-model="instance"
              :options="instances"
              :placeholder="$t('oauths.select_instance')"
              track-by="id"
              value="id"
              label="name"
              :allow-empty="false"
              :selectLabel="''"
              :deselectLabel="''"
              :selectedLabel="''"
              :disabled="disabled"
              @input="
                pagination.page = 1;
                onInstanceChange();
              "
            ></vue-multiselect>
          </div>
        </b-form>

        <b-button
          class="mt-2 btn btn-success"
          v-text="$t('buttons.create')"
          :disabled="disabled || !(permissions.indexOf('oauth-create') !== -1)"
          :href="
            $routes('admin.oauths.create', {
              instance: instance ? instance.id : null,
            })
          "
        />

        <common-loading
          class="my-2"
          :message="$t('oauths.fetching_instances')"
          v-if="fetching_instances"
        />

        <b-alert variant="danger" :show="errorMessage != null" class="mt-2">
          <em class="fa fa-times-circle mr-2"></em>
          {{ errorMessage }}
        </b-alert>

        <b-alert
          variant="warning"
          :show="!instance && !fetching_instances"
          class="mt-2"
        >
          <em class="fa fa-info-circle mr-2"></em>
          {{ $t("oauths.select_instance_msg") }}
        </b-alert>

        <div class="float-right my-2">
          <em class="text-info" v-text="`Page: ${pagination.page}`" />

          <b-form-select
            class="w-auto ml-2"
            variant="primary"
            v-model="pagination.limit"
            :options="pagination.pageOptions"
            @change="setPage(1)"
            :disabled="fetching_oauths || !instance"
          ></b-form-select>

          <b-button-group>
            <b-button
              variant="primary"
              class="mr-1"
              v-text="'<<'"
              :disabled="fetching_oauths || pagination.page == 1"
              @click="setPage(1)"
            />
            <b-button
              variant="primary"
              class="mr-1"
              v-text="'<'"
              :disabled="fetching_oauths || pagination.page == 1"
              @click="setPage(pagination.page - 1)"
            />
            <b-button
              variant="primary"
              class="mr-1"
              v-text="'>'"
              :disabled="
                fetching_oauths || (oauths && oauths.length < pagination.limit)
              "
              @click="setPage(pagination.page + 1)"
            />
          </b-button-group>
        </div>

        <b-table
          striped
          hover
          bordered
          show-empty
          fixed
          thead-class="table-primary"
          :items="oauths"
          :fields="fields"
          :busy="fetching_oauths"
          class="mt-2"
        >
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
              <strong>{{ $t("commons.loading") }}</strong>
            </div>
          </template>

          <template v-slot:cell(actions)="row">
            <b-button
              variant="info"
              v-if="permissions.indexOf('oauth-edit') === -1"
              :disabled="!(permissions.indexOf('oauth-read') !== -1)"
              :href="
                $routes('admin.oauths.edit', {
                  id: row.item.id,
                  instance: instance.id,
                })
              "
              class="m-1"
            >
              <i class="fa fa-eye"></i>
            </b-button>
            <b-button
              v-if="permissions.indexOf('oauth-edit') !== -1"
              variant="primary"
              :disabled="
                !(
                  permissions.indexOf('oauth-edit') !== -1 &&
                  permissions.indexOf('oauth-read') !== -1
                )
              "
              :href="
                $routes('admin.oauths.edit', {
                  id: row.item.id,
                  instance: instance.id,
                })
              "
            >
              <em class="fa fa-edit"></em>
            </b-button>
          </template>
        </b-table>

        <confirmation-modal ref="confirmModal"></confirmation-modal>
      </div>
    </div>
  </div>
</template>

<script>
import InstancesApi from "../../../apis/InstancesApi";
import OauthsApi from "../../../apis/OauthsApi";

export default {
  props: {
    params: Object | Array,
    permissions: Object | Array,
  },
  created() {
    this.getInstances();
  },
  data() {
    return {
      instances: [],
      instance: null,
      errorMessage: null,
      loading: false,
      deleting: false,
      fetching_oauths: false,
      fetching_instances: false,
      oauths: [],
      pagination: {
        page: 1,
        limit: 10,
        pageOptions: [10, 25, 50, 100],
      },
      fields: [
        {
          key: "id",
          label: this.$t("commons.id"),
        },
        "name",
        {
          key: "isActive",
          label: this.$t("commons.active"),
        },
        {
          key: "createTime",
          label: this.$t("commons.created_time"),
          formatter: (value) => {
            return this.$options.filters.datetime(value);
          },
        },
        {
          key: "lastModifiedTime",
          label: this.$t("commons.last_modified"),
          formatter: (value) => {
            return this.$options.filters.datetime(value);
          },
        },
        {
          key: "actions",
          label: this.$t("commons.actions"),
        },
      ],
    };
  },
  computed: {
    disabled() {
      return this.fetching_instances || this.fetching_oauths || this.deleting;
    },
    isHermesAdminInstance() {
      return this.instance == window.Laravel.admin_instance;
    },
  },
  methods: {
    getInstances() {
      this.fetching_instances = true;
      InstancesApi.all({
        limit: 10000,
        orderBy: "name",
        returns: "id,name",
      })
        .then((r) => {
          this.fetching_instances = false;
          this.instances = r.result;

          if (!this.instance && this.params && this.params.instance) {
            this.instance = this.instances.find(
              (i) => i.id == this.params.instance
            );
            this.onInstanceChange();
          }
        })
        .catch((e) => {
          this.fetching_instances = false;
          this.$toast.error(
            "Error",
            this.$t("commons.unable_to_fetch_instances")
          );
        });
    },
    onInstanceChange() {
      this.fetching_oauths = true;
      let params = {
        limit: this.pagination.limit,
        offset: (this.pagination.page - 1) * this.pagination.limit,
      };
      OauthsApi.all(params, this.instance.id)
        .then((r) => {
          this.fetching_oauths = false;
          this.oauths = r.result;
        })
        .catch((error) => {
          this.fetching_oauths = false;
          this.$toast.error("ERROR", this.$t("oauths.get_all_failed"));
        });
    },
    setPage(page) {
      this.pagination.page = page;
      this.onInstanceChange();
    },
  },
};
</script>
